import { getPreferredUsername } from '../auth-store';
import { CamundaBaseService } from './base-service';
import type { CamundaGroup, CamundaUser } from './types';

const SYSTEM_USER_IDS = new Set(['deleter', 'system']);
const USER_GROUPS_CACHE_TTL_MS = 2 * 60 * 1000;

type UserGroupsCacheEntry = {
  expiresAt: number;
  groups: CamundaGroup[];
};

const USER_PAGE_SIZE = 100;
const DEFAULT_SEARCH_MAX_RESULTS = 100;

function sortUsersByDisplayName(users: CamundaUser[]): CamundaUser[] {
  return users.toSorted((a, b) => {
    const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.id;
    const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim() || b.id;
    return nameA.localeCompare(nameB);
  });
}

function excludeSystemUsers(users: CamundaUser[]): CamundaUser[] {
  return users.filter((user) => !SYSTEM_USER_IDS.has(user.id));
}

export class CamundaUserService extends CamundaBaseService {
  private static readonly userGroupsCache = new Map<string, UserGroupsCacheEntry>();
  private static readonly userGroupsInFlight = new Map<string, Promise<CamundaGroup[]>>();

  static getCurrentUserId(): string | null {
    return getPreferredUsername();
  }

  static async getCurrentUser(): Promise<CamundaUser | null> {
    try {
      const userId = this.getCurrentUserId();
      if (!userId || SYSTEM_USER_IDS.has(userId)) return null;

      return await this.fetchUserProfile(userId);
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  }

  private static async fetchUserProfile(userId: string): Promise<CamundaUser | null> {
    if (SYSTEM_USER_IDS.has(userId)) return null;
    const url = this.getApiUrl(`/user/${userId}/profile`);
    const authHeaders = await this.getAuthHeaders();
    const headers = new Headers(authHeaders);
    headers.set('Content-Type', 'application/json');
    const response = await fetch(url, {
      headers,
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`Error fetching user ${userId}: ${response.status} ${response.statusText}`, errorBody);
      return null;
    }
    return response.json();
  }

  static getCurrentUserFullName(): string | null {
    try {
      const userFullName = localStorage.getItem('camundaUserFullName');
      return userFullName;
    } catch {
      return null;
    }
  }

  static async getUserGroups(userId: string): Promise<CamundaGroup[]> {
    if (!userId) {
      return [];
    }

    const now = Date.now();
    const cached = this.userGroupsCache.get(userId);
    if (cached && cached.expiresAt > now) {
      return [...cached.groups];
    }

    const inFlight = this.userGroupsInFlight.get(userId);
    if (inFlight) {
      const groups = await inFlight;
      return [...groups];
    }

    const request = this.fetchUserGroups(userId);
    this.userGroupsInFlight.set(userId, request);

    try {
      const groups = await request;
      return [...groups];
    } finally {
      this.userGroupsInFlight.delete(userId);
    }
  }

  private static async fetchUserGroups(userId: string): Promise<CamundaGroup[]> {
    try {
      const params = new URLSearchParams({ userId });
      const url = `/identity/groups?${params}`;

      const result = await this.makeRequest<{ groups?: CamundaGroup[] }>(url);
      const groups = Array.isArray(result?.groups) ? result.groups : [];
      this.userGroupsCache.set(userId, {
        expiresAt: Date.now() + USER_GROUPS_CACHE_TTL_MS,
        groups: [...groups],
      });
      return groups;
    } catch (error) {
      this.userGroupsCache.delete(userId);
      console.error('Error fetching user groups:', error);
      return [];
    }
  }

  static async listUsersByGroup(groupId: string): Promise<CamundaUser[]> {
    try {
      const params = new URLSearchParams({ memberOfGroup: groupId });
      const users = await this.makeRequest<CamundaUser[]>(`/user?${params}`);
      if (!Array.isArray(users)) {
        return [];
      }

      return sortUsersByDisplayName(users);
    } catch (error) {
      console.error(`Error fetching users for group ${groupId}:`, error);
      return [];
    }
  }

  static async listUsers(options?: { maxResults?: number }): Promise<CamundaUser[]> {
    const maxResults = options?.maxResults;
    const pageSize = maxResults != null ? Math.min(maxResults, USER_PAGE_SIZE) : USER_PAGE_SIZE;

    try {
      const allUsers: CamundaUser[] = [];
      let firstResult = 0;

      while (true) {
        const params = new URLSearchParams({
          firstResult: String(firstResult),
          maxResults: String(pageSize),
          sortBy: 'lastName',
          sortOrder: 'asc',
        });
        const batch = await this.makeRequest<CamundaUser[]>(`/user?${params}`);
        if (!Array.isArray(batch) || batch.length === 0) {
          break;
        }

        allUsers.push(...excludeSystemUsers(batch));

        if (maxResults != null && allUsers.length >= maxResults) {
          return sortUsersByDisplayName(allUsers.slice(0, maxResults));
        }

        if (batch.length < pageSize) {
          break;
        }

        firstResult += pageSize;
      }

      return sortUsersByDisplayName(allUsers);
    } catch (error) {
      console.error('Error fetching Camunda users:', error);
      return [];
    }
  }

  static async searchUsers(
    searchTerm: string,
    maxResults: number = DEFAULT_SEARCH_MAX_RESULTS,
  ): Promise<CamundaUser[]> {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      return this.listUsers({ maxResults });
    }

    const likePattern = `%${trimmed}%`;
    const cappedMax = String(maxResults);

    try {
      const queries = [
        new URLSearchParams({
          firstNameLike: likePattern,
          maxResults: cappedMax,
          sortBy: 'lastName',
          sortOrder: 'asc',
        }),
        new URLSearchParams({
          lastNameLike: likePattern,
          maxResults: cappedMax,
          sortBy: 'lastName',
          sortOrder: 'asc',
        }),
        new URLSearchParams({
          emailLike: likePattern,
          maxResults: cappedMax,
          sortBy: 'lastName',
          sortOrder: 'asc',
        }),
        new URLSearchParams({
          id: trimmed,
          maxResults: cappedMax,
        }),
      ];

      const results = await Promise.all(
        queries.map((params) => this.makeRequest<CamundaUser[]>(`/user?${params}`)),
      );

      const byId = new Map<string, CamundaUser>();
      for (const batch of results) {
        if (!Array.isArray(batch)) continue;
        for (const user of excludeSystemUsers(batch)) {
          if (!byId.has(user.id)) {
            byId.set(user.id, user);
          }
        }
      }

      return sortUsersByDisplayName([...byId.values()]).slice(0, maxResults);
    } catch (error) {
      console.error('Error searching Camunda users:', error);
      return [];
    }
  }

  static async getUserById(userId: string): Promise<CamundaUser | null> {
    if (!userId || SYSTEM_USER_IDS.has(userId)) {
      return null;
    }
    try {
      const result = await this.makeRequest<CamundaUser>(`/user/${userId}/profile`);
      return result ?? null;
    } catch (error) {
      const is404 =
        error instanceof Error &&
        (error.message.includes('404') || error.message.includes('Not Found'));
      if (!is404) {
        console.error('Error fetching user by ID:', error);
      }
      return null;
    }
  }
}
